import { autocomplete } from '@algolia/autocomplete-js';
import '@algolia/autocomplete-theme-classic';
import { createLocalStorageRecentSearchesPlugin } from '@algolia/autocomplete-plugin-recent-searches';

// Helper: debounce de promesas
function debouncePromise(fn, time) {
    let timer = undefined;
    return function debounced(...args) {
        if (timer) clearTimeout(timer);
        return new Promise((resolve) => {
            timer = setTimeout(() => resolve(fn(...args)), time);
        });
    };
}

const translations = window.translations || {};

/* Medicion del buscador.
   El autocompletado consulta a cada pulsacion, con solo 200 ms de respiro, asi que enviar un
   evento por consulta llenaria la analitica de terminos a medio escribir: "c", "ci", "cir". Se
   espera a que el visitante pare de teclear, se exige una longitud minima y no se repite el
   ultimo termino medido, que es lo que pasaria al borrar una letra y volver a escribirla. */
const SEARCH_TRACK_DELAY = 1200;
const SEARCH_TRACK_MIN_LENGTH = 3;

let searchTrackTimer = null;
let lastTrackedSearch = '';

/**
 * Empuja el evento por la puerta del plugin de analitica, o directamente a la capa de datos si
 * ese plugin no esta activo. El theme no deberia dejar de medir porque falte un plugin.
 */
function pushSearchEvent(event, payload) {

    if (typeof window.scdl === 'function') {
        window.scdl(event, payload);
        return;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event }, payload));
}

/**
 * Anota una busqueda y, si no devolvio nada, tambien el hecho de que no devolviera nada.
 *
 * Son dos eventos a proposito: uno cuenta cuanto se busca y el otro se puede convertir en alerta
 * o en evento clave sin tener que filtrar por un parametro.
 */
function trackSearch(query, resultCount) {

    const term = (query || '').trim().toLowerCase();

    if (term.length < SEARCH_TRACK_MIN_LENGTH) {
        return;
    }

    clearTimeout(searchTrackTimer);

    searchTrackTimer = setTimeout(() => {

        if (term === lastTrackedSearch) {
            return;
        }

        lastTrackedSearch = term;

        pushSearchEvent('search', { search_term: term, resultados: resultCount });

        if (resultCount === 0) {
            pushSearchEvent('no_search_results', { search_term: term, resultados: 0 });
        }
    }, SEARCH_TRACK_DELAY);
}

// === Acción principal al seleccionar/confirmar una búsqueda ===
function applySearch(q) {
    if (typeof window.doSearch === 'function') {
        window.doSearch(q);
        return;
    }

    const base = translations.search_url || '/buscar/';
    const url = new URL(base, window.location.origin);
    url.searchParams.set('q', q);
    window.location.assign(url.toString());
}

// Plugin de búsquedas recientes
const recentSearchesPlugin = createLocalStorageRecentSearchesPlugin({
    key: 'RECENT_SEARCH',
    limit: 5,
    transformSource({ source }) {
        return {
        ...source,
        getItemInputValue({ item }) {
            return item.label || item.query || String(item);
        },
        onSelect({ item, setQuery, setIsOpen }) {
            const q = item.label || item.query || '';
            setQuery(q);
            setTimeout(() => setIsOpen(true), 0);
            // applySearch(q);
        },
        templates: {
            item(params) {
            const { item, html } = params;
            return html`<div class="aa-ItemLink">
                ${source.templates.item(params).props.children}
            </div>`;
            },
        },
        };
    },
});

/**
 * Consultas populares para sugerir al abrir el buscador, o un array vacío si Typesense
 * no contesta.
 *
 * El endpoint responde 500 con {error: true, message} cuando no puede conectar con
 * Typesense — en local no está levantado (su servicio está comentado en el
 * docker-compose), y en producción pasaría lo mismo si el servicio se cayera. Antes se
 * hacía `.hits.map()` directamente sobre esa respuesta, así que `.hits` era undefined y
 * lanzaba un TypeError sin capturar que dejaba el autocompletado a medias.
 *
 * Las sugerencias son un extra, no el buscador: si no llegan, se degrada en silencio.
 */
async function fetchPopularQueries() {
    try {
        /* Proxy ligero (api/buscador.php): la REST de WordPress tardaba 2 s solo en arrancar */
        const response = await fetch('/api/buscador.php?popular=1');

        if (!response.ok) return [];

        const data = await response.json();

        if (!Array.isArray(data?.hits)) return [];

        return data.hits
            .map((hit) => ({ label: hit?.document?.query }))
            .filter((suggestion) => Boolean(suggestion.label));
    } catch (error) {
        return [];
    }
}

// === Sources: servidor/proxy Typesense ===
async function fetchGroupedSources({ query }) {
    // Sugerencias (consultas populares dinámicas) cuando no hay query
    if (!query) {
        const suggestions = await fetchPopularQueries();

        // Sin sugerencias no se devuelve la fuente: el buscador sigue funcionando igual,
        // solo no propone nada al abrirlo.
        if (!suggestions.length) return [];

        return [
            {
                sourceId: 'suggested-queries',
                getItems() {
                    return suggestions;
                },
                getItemInputValue({ item }) {
                    return item.label;
                },
                onSelect({ item, setQuery, setIsOpen }) {
                    const q = item.label || '';
                    setQuery(q);
                    setTimeout(() => setIsOpen(true), 0);
                    //   applySearch(q);
                },
                templates: {
                    item({ item, html }) {
                        setTimeout(() => lucide?.createIcons?.(), 0);
                        return html`<div class="aa-ItemContent">
                            <div class="aa-ItemIcon aa-ItemIcon--noBorder">
                            <svg class="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M16.041 15.856c-0.034 0.026-0.067 0.055-0.099 0.087s-0.060 0.064-0.087 0.099c-1.258 1.213-2.969 1.958-4.855 1.958-1.933 0-3.682-0.782-4.95-2.050s-2.050-3.017-2.050-4.95 0.782-3.682 2.050-4.95 3.017-2.050 4.95-2.050 3.682 0.782 4.95 2.050 2.050 3.017 2.050 4.95c0 1.886-0.745 3.597-1.959 4.856zM21.707 20.293l-3.675-3.675c1.231-1.54 1.968-3.493 1.968-5.618 0-2.485-1.008-4.736-2.636-6.364s-3.879-2.636-6.364-2.636-4.736 1.008-6.364 2.636-2.636 3.879-2.636 6.364 1.008 4.736 2.636 6.364 3.879 2.636 6.364 2.636c2.125 0 4.078-0.737 5.618-1.968l3.675 3.675c0.391 0.391 1.024 0.391 1.414 0s0.391-1.024 0-1.414z"></path></svg>
                            </div>
                            <div class="aa-ItemContentBody">
                            <div class="aa-ItemContentTitle text-sm">${item.label}</div>
                            </div>
                        </div>
                        <div class="aa-ItemActions">
                            <button type="button" class="aa-ItemActionButton" title="Aplicar búsqueda">
                            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 17v-7.586l8.293 8.293c0.391 0.391 1.024 0.391 1.414 0s0.391-1.024 0-1.414l-8.293-8.293h7.586c0.552 0 1-0.448 1-1s-0.448-1-1-1h-10c-0.552 0-1 0.448-1 1v10c0 0.552 0.448 1 1 1s1-0.448 1-1z"></path></svg>
                            </button>
                        </div>`;
                    },
                },
            },
        ];
    }

    // Búsqueda en proxy Typesense
    let lang = jQuery('html').attr('lang');
    let shortLang = lang ? lang.split('-')[0] : null;

    const response = await fetch('/api/buscador.php?q=' + encodeURIComponent(query) + '&lang=' + encodeURIComponent(shortLang));

    // Mismo motivo que en fetchPopularQueries(): si Typesense no contesta esto llega como
    // un 500 con {error: true}. El `results.hits || []` de abajo ya lo aguantaba, pero un
    // 500 que no sea JSON haría fallar el .json(), asi que se comprueba antes.
    const results = response.ok ? await response.json().catch(() => ({})) : {};
    const hits = results.hits || [];

    /* Se mide aqui y no en el input porque este es el unico punto que conoce a la vez el termino
       y cuantos resultados ha devuelto: sin las dos cosas no se puede distinguir una busqueda
       util de una que no encontro nada. */
    trackSearch(query, hits.length);

    const grouped = { authors: [], pages: [], products: [], others: [] };

    hits.forEach((hit) => {

        const doc = hit.document;

        switch (doc.post_type) {
            case 'creator':
                grouped.authors.push(hit);
                break;

            case 'product':
                grouped.products.push(hit);
                break;

            // Todo lo que no es producto ni autor va al grupo "Pages", con su etiqueta de
            // tipo al lado. `project` estaba indexado pero no salia: caia en `others`, que
            // no se pinta. Familias y colecciones son terminos, no posts, pero para el
            // buscador son una entrada mas (ver sc_typesense_build_term_document).
            case 'page':
            case 'story':
            case 'exhibition':
            case 'project':
            case 'product_family':
            case 'product_collection':
                grouped.pages.push(hit);
                break;

            default:
                grouped.others.push(hit);
                break;
        }

    });

    const sources = [];

    if (grouped.authors.length > 0) {
        sources.push({
            sourceId: 'author',
            getItems() {
                return grouped.authors;
            },
            onSelect({ item }) {
                // Registrar popular queries
                const payload = JSON.stringify({ query: item.document.title });
                navigator.sendBeacon(
                    '/wp-json/typesense/update-popular-queries',
                    new Blob([payload], { type: 'application/json' })
                );
            },
            templates: {
                header() {
                    return window.translations.ts_group_authors;
                },
                item({ item, html }) {
                    const doc = item.document;
                    const highlightTitle = item.highlights.find((h) => h.field === 'title')?.value || doc.title;
                    setTimeout(() => lucide?.createIcons?.(), 0);

                    return html`<div class="aa-ItemWrapper">
                        <div class="aa-ItemContent">
                            <a class="aa-ItemLink" href="${doc.link}" target="_self">
                            <div class="aa-ItemContentBody !flex !gap-1">
                                <div class="aa-ItemIcon aa-ItemIcon--noBorder">
                                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M16.041 15.856c-0.034 0.026-0.067 0.055-0.099 0.087s-0.060 0.064-0.087 0.099c-1.258 1.213-2.969 1.958-4.855 1.958-1.933 0-3.682-0.782-4.95-2.050s-2.050-3.017-2.050-4.95 0.782-3.682 2.050-4.95 3.017-2.050 4.95-2.050 3.682 0.782 4.95 2.050 2.050 3.017 2.050 4.95c0 1.886-0.745 3.597-1.959 4.856zM21.707 20.293l-3.675-3.675c1.231-1.54 1.968-3.493 1.968-5.618 0-2.485-1.008-4.736-2.636-6.364s-3.879-2.636-6.364-2.636-4.736 1.008-6.364 2.636-2.636 3.879-2.636 6.364 1.008 4.736 2.636 6.364 3.879 2.636 6.364 2.636c2.125 0 4.078-0.737 5.618-1.968l3.675 3.675c0.391 0.391 1.024 0.391 1.414 0s0.391-1.024 0-1.414z"></path></svg>
                                </div>
                                <div class="aa-ItemContentTitle" dangerouslySetInnerHTML=${{ __html: highlightTitle }}></div>
                            </div>
                            </a>
                        </div>
                        <div class="aa-ItemActions">
                            <button class="aa-ItemActionButton aa-DesktopOnly aa-ActiveOnly" type="button" title="Select">
                            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                                <path d="M18.984 6.984h2.016v6h-15.188l3.609 3.609-1.406 1.406-6-6 6-6 1.406 1.406-3.609 3.609h13.172v-4.031z" />
                            </svg>
                            </button>
                        </div>
                        </div>`;
                },
            },
        });
    }

    if (grouped.pages.length > 0) {
        sources.push({
            sourceId: 'pages',
            getItems() {
                return grouped.pages;
            },
            onSelect({ item }) {
                // Registrar popular queries
                const payload = JSON.stringify({ query: item.document.title });
                navigator.sendBeacon(
                    '/wp-json/typesense/update-popular-queries',
                    new Blob([payload], { type: 'application/json' })
                );
            },
            templates: {
                header() {
                    return window.translations.ts_group_pages;
                },
                item({ item, html }) {
                    const doc = item.document;
                    const highlightTitle = item.highlights.find((h) => h.field === 'title')?.value || doc.title;
                    const typeLabel = doc.post_type_label;
                    setTimeout(() => lucide?.createIcons?.(), 0);

                    /* return html`<div class="aa-ItemWrapper">
                        <div class="aa-ItemContent">
                            <a class="aa-ItemLink" href="${doc.link}" target="_blank" rel="noopener noreferrer">
                            <div class="aa-ItemContentBody !flex !gap-1">
                                <div class="aa-ItemIcon aa-ItemIcon--noBorder">
                                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M16.041 15.856c-0.034 0.026-0.067 0.055-0.099 0.087s-0.060 0.064-0.087 0.099c-1.258 1.213-2.969 1.958-4.855 1.958-1.933 0-3.682-0.782-4.95-2.050s-2.050-3.017-2.050-4.95 0.782-3.682 2.050-4.95 3.017-2.050 4.95-2.050 3.682 0.782 4.95 2.050 2.050 3.017 2.050 4.95c0 1.886-0.745 3.597-1.959 4.856zM21.707 20.293l-3.675-3.675c1.231-1.54 1.968-3.493 1.968-5.618 0-2.485-1.008-4.736-2.636-6.364s-3.879-2.636-6.364-2.636-4.736 1.008-6.364 2.636-2.636 3.879-2.636 6.364 1.008 4.736 2.636 6.364 3.879 2.636 6.364 2.636c2.125 0 4.078-0.737 5.618-1.968l3.675 3.675c0.391 0.391 1.024 0.391 1.414 0s0.391-1.024 0-1.414z"></path></svg>
                                </div>
                                <div class="aa-ItemContentTitle" dangerouslySetInnerHTML=${{ __html: highlightTitle }}></div>
                            </div>
                            </a>
                        </div>
                        <div class="aa-ItemActions">
                            <button class="aa-ItemActionButton aa-DesktopOnly aa-ActiveOnly" type="button" title="Select">
                            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                                <path d="M18.984 6.984h2.016v6h-15.188l3.609 3.609-1.406 1.406-6-6 6-6 1.406 1.406-3.609 3.609h13.172v-4.031z" />
                            </svg>
                            </button>
                        </div>
                        </div>`; */

                    return html`<div class="aa-ItemWrapper">
                        <div class="aa-ItemContent">
                            <a class="aa-ItemLink" href="${doc.link}" target="_self">
                                <div class="aa-ItemContentBody !flex !items-center !gap-2">
                                    <div class="aa-ItemIcon aa-ItemIcon--noBorder">
                                        <svg viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4"><path d="M16.041 15.856c-0.034 0.026-0.067 0.055-0.099 0.087s-0.060 0.064-0.087 0.099c-1.258 1.213-2.969 1.958-4.855 1.958-1.933 0-3.682-0.782-4.95-2.050s-2.050-3.017-2.050-4.95 0.782-3.682 2.050-4.95 3.017-2.050 4.95-2.050 3.682 0.782 4.95 2.050 2.050 3.017 2.050 4.95c0 1.886-0.745 3.597-1.959 4.856zM21.707 20.293l-3.675-3.675c1.231-1.54 1.968-3.493 1.968-5.618 0-2.485-1.008-4.736-2.636-6.364s-3.879-2.636-6.364-2.636-4.736 1.008-6.364 2.636-2.636 3.879-2.636 6.364 1.008 4.736 2.636 6.364 3.879 2.636 6.364 2.636c2.125 0 4.078-0.737 5.618-1.968l3.675 3.675c0.391 0.391 1.024 0.391 1.414 0s0.391-1.024 0-1.414z"></path></svg>
                                    </div>
                                    <div class="aa-ItemContentTitle flex items-center gap-1.5 text-sm">
                                        <span dangerouslySetInnerHTML=${{ __html: highlightTitle }}></span>
                                        ${typeLabel && html`<span class="text-sm text-gray-400 font-normal">  —  in ${typeLabel}</span>`}
                                    </div>
                                </div>
                            </a>
                        </div>
                    </div>`;
                },
            },
        });
    }

    if (grouped.products.length > 0) {
        sources.push({
            sourceId: 'products',
            getItems() {
                return grouped.products;
            },
            onSelect({ item }) {
                const payload = JSON.stringify({ query: item.document.title });
                navigator.sendBeacon(
                '/wp-json/typesense/update-popular-queries',
                new Blob([payload], { type: 'application/json' })
                );
                // Navega por el <a> del template
            },
            templates: {
                header() {
                return 'Products';
                },
                item({ item, html }) {
                const doc = item.document;
                const image =
                    doc.image || (translations.home_url || '/') + 'wp-content/uploads/2025/07/Placeholder-image.jpg';
                return html`<div class="aa-ItemWrapper">
                    <div class="aa-ItemContent">
                        <a class="aa-ItemProduct" href="${doc.link}" target="_self" rel="noopener noreferrer">
                        <div class="aa-ItemIcon">
                            <img src="${image}" alt="${doc.title}" width="40" height="40" class="aspect-3/4 object-cover" />
                        </div>
                        <div class="aa-ItemContentBody flex flex-col py-4 !gap-0">
                            <div class="aa-ItemContentTitle product-author">
                            ${doc.author &&
                            html`<span dangerouslySetInnerHTML=${{
                                __html: `<span class="text-sm text-sc-color-muted-foreground leading-5">${doc.author}</span>`,
                            }} />`}
                            </div>
                            <div class="aa-ItemContentTitle product-title">
                            ${doc.title &&
                            html`<span dangerouslySetInnerHTML=${{
                                __html: `<span class="text-sm text-sc-color-primary text-sc-typography-button-font-size-sm font-sc-typography-button-font-weight-sm leading-sc-typography-button-line-height-sm">${doc.title}</span>`,
                            }} />`}
                            </div>
                            <div class="aa-ItemContentTitle product-year">
                            ${doc.year &&
                            html`<span dangerouslySetInnerHTML=${{
                                __html: `<span class="text-xs text-sc-color-muted-foreground leading-5">${doc.year}</span>`,
                            }} />`}
                            </div>
                        </div>
                        </a>
                    </div>
                    </div>`;
                },
            },
        });
    }

    return sources;
}

const debouncedFetchGroupedSources = debouncePromise(fetchGroupedSources, 200);

jQuery(document).ready(function ($) {
    window.autocompleteInstance = autocomplete({
        container: '#autocomplete',
        placeholder: translations.search__placeholder,
        plugins: [recentSearchesPlugin],
        openOnFocus: true,
        /* debug: true, */
        insights: true,
        getSources: debouncedFetchGroupedSources,
        classNames: {
            detachedSearchButton:
                '!pt-6.5 !pb-6 !px-2 !border-0 !border-b !border-solid !border-b-sc-color-border !text-sc-color-popover-foreground',
            detachedSearchButtonIcon: 'opacity-20',
            detachedSearchButtonPlaceholder: 'opacity-20 text-sm',
        },
        onSubmit({ state, event, setIsOpen }) {
            event.preventDefault();
            const q = state.query || '';
            setTimeout(() => setIsOpen(true), 0);
            //   applySearch(q);
        },
    });
});
